// Client Portal — Telegram bot portal. Extracted from App.js (was defined
// inside App() but is fully prop-driven, so it lifts out cleanly).
import { useState, useEffect } from "react";
import {
  AlertTriangle,
  Check,
  MessageSquare,
  RefreshCw,
  Save,
  Settings,
  Trash2,
  X,
} from "lucide-react";
import { authFetch } from "../lib/api";
import { KB_ROLE_LEVELS } from "./RAGChat";
import { SystemBanner } from "../components/SystemBanner";
import { WizardShell } from "../components/WizardShell";

export const TelegramBotPortal = ({
  facility,
  user,
  systemMessages: msgs = [],
  onDismissMessage,
}) => {
  const svc = facility.services?.find((s) => s.service_type === "telegram_bot");
  const kbRole = facility.kb_role || svc?.access_role || "viewer";
  const kbRoleGte = (required) =>
    (KB_ROLE_LEVELS[kbRole] ?? 0) >= (KB_ROLE_LEVELS[required] ?? 99);
  const isAdmin = kbRoleGte("admin");

  const [tgTab, setTgTab] = useState("violations");
  const [violations, setViolations] = useState([]);
  const [violationsTotal, setViolationsTotal] = useState(0);
  const [vLoading, setVLoading] = useState(false);
  const [reviewFilter] = useState("false");
  const [, setReviewingId] = useState(null);
  const [unbanningId, setUnbanningId] = useState(null);
  const [expandedV, setExpandedV] = useState(null);

  // Posts tab state
  const [posts, setPosts] = useState([]);
  const [postsTotal, setPostsTotal] = useState(0);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsRefresh, setPostsRefresh] = useState(0);
  const [postsSource, setPostsSource] = useState(""); // "" | "capture" | "log"
  const [deletingPostId, setDeletingPostId] = useState(null);
  const [qaModal, setQaModal] = useState(null); // {post} or null
  const [qaForm, setQaForm] = useState({ question: "", answer: "" });
  const [qaSaving, setQaSaving] = useState(false);
  const [qaMsg, setQaMsg] = useState(null);

  const [cfg, setCfg] = useState({
    group_id: "",
    modes: { moderation: true, qa: true, logging: false },
    respond_to_messages: true,
    daily_message_limit: 50,
    prohibited_words: [],
  });
  const setMode = (key, on) =>
    setCfg((prev) => ({
      ...prev,
      modes: { ...prev.modes, [key]: on },
    }));
  const [cfgLoading, setCfgLoading] = useState(false);
  const [cfgSaving, setCfgSaving] = useState(false);
  const [cfgSaved, setCfgSaved] = useState(false);
  const [cfgError, setCfgError] = useState(null);
  const [clearingRedis, setClearingRedis] = useState(false);
  const [clearRedisMsg, setClearRedisMsg] = useState(null);

  const fid = facility.facility_id;

  useEffect(() => {
    if (tgTab !== "violations") return;
    setVLoading(true);
    const p = new URLSearchParams({ facility_id: fid, limit: "100" });
    authFetch(`/tg-violations?${p}`)
      .then((d) => {
        setViolations(d.violations || []);
        setViolationsTotal(d.total || 0);
      })
      .catch(() => {})
      .finally(() => setVLoading(false));
  }, [tgTab, fid, reviewFilter]);

  useEffect(() => {
    if (tgTab !== "posts") return;
    setPostsLoading(true);
    const src = postsSource ? `&source=${postsSource}` : "";
    authFetch(`/tg-posts?facility_id=${fid}&limit=100${src}`)
      .then((d) => {
        setPosts(d.posts || []);
        setPostsTotal(d.total || 0);
      })
      .catch(() => {})
      .finally(() => setPostsLoading(false));
  }, [tgTab, fid, postsRefresh, postsSource]);

  useEffect(() => {
    if (tgTab !== "config" || !isAdmin) return;
    setCfgLoading(true);
    authFetch(`/telegram-config?facility_id=${fid}`)
      .then((d) => setCfg((prev) => ({ ...prev, ...d })))
      .catch(() => {})
      .finally(() => setCfgLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tgTab, fid]);

  const handleReview = async (id) => {
    setReviewingId(id);
    const d = await authFetch(`/tg-violations/${id}/review`, {
      method: "POST",
      body: JSON.stringify({}),
    });
    if (!d.error)
      setViolations((prev) =>
        reviewFilter === "false"
          ? prev.filter((v) => v.id !== id)
          : prev.map((v) => (v.id === id ? { ...v, reviewed: true } : v)),
      );
    setReviewingId(null);
  };

  const handleSaveCfg = async () => {
    setCfgSaving(true);
    setCfgError(null);
    setCfgSaved(false);
    try {
      const d = await authFetch(`/telegram-config`, {
        method: "POST",
        body: JSON.stringify({ facility_id: fid, ...cfg }),
      });
      if (d.error) throw new Error(d.error);
      setCfgSaved(true);
      setTimeout(() => setCfgSaved(false), 3000);
    } catch (err) {
      setCfgError(err.message);
    } finally {
      setCfgSaving(false);
    }
  };

  const handleUnban = async (v) => {
    if (!v.user_id) return;
    setUnbanningId(v.id);
    await authFetch(`/tg-unban`, {
      method: "POST",
      body: JSON.stringify({
        facility_id: fid,
        user_id: v.user_id,
        group_id: v.chat_id,
      }),
    }).catch(() => {});
    setUnbanningId(null);
  };

  const handleDeletePost = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    setDeletingPostId(id);
    const d = await authFetch(`/tg-posts/${id}`, { method: "DELETE" }).catch(
      () => null,
    );
    if (d && !d.error) setPosts((prev) => prev.filter((p) => p.id !== id));
    setDeletingPostId(null);
  };

  const handleOpenQa = (post) => {
    setQaForm({ question: "", answer: post.notes || "" });
    setQaMsg(null);
    setQaModal(post);
  };

  const handleSaveQa = async (e) => {
    e.preventDefault();
    setQaSaving(true);
    setQaMsg(null);
    try {
      const d = await authFetch(`/tg-qa`, {
        method: "POST",
        body: JSON.stringify({
          facility_id: fid,
          post_id: qaModal.id,
          question: qaForm.question,
          answer: qaForm.answer,
        }),
      });
      if (d.error) throw new Error(d.error);
      setQaMsg({ type: "ok", text: `Q&A saved (doc #${d.id})` });
      setTimeout(() => setQaModal(null), 1500);
    } catch (err) {
      setQaMsg({ type: "err", text: err.message });
    } finally {
      setQaSaving(false);
    }
  };

  const handleClearRedis = async () => {
    if (
      !window.confirm(
        "Clear all Telegram monitoring data from Redis for this facility? This resets daily counters and rate limits.",
      )
    )
      return;
    setClearingRedis(true);
    setClearRedisMsg(null);
    try {
      const d = await authFetch(`/tg-clear-redis`, {
        method: "POST",
        body: JSON.stringify({ facility_id: fid }),
      });
      if (d.error) throw new Error(d.error);
      setClearRedisMsg(`Cleared ${d.deleted} key${d.deleted !== 1 ? "s" : ""}`);
      setTimeout(() => setClearRedisMsg(null), 4000);
    } catch (err) {
      setClearRedisMsg(`Error: ${err.message}`);
    } finally {
      setClearingRedis(false);
    }
  };

  const ACTION_COLORS = {
    warn: "bg-yellow-100 text-yellow-700",
    mute: "bg-orange-100 text-orange-700",
    ban: "bg-red-100 text-red-700",
  };

  const tabs = [
    { id: "violations", label: "Violations", icon: AlertTriangle },
    { id: "posts", label: "Posts", icon: MessageSquare },
    ...(isAdmin ? [{ id: "config", label: "Config", icon: Settings }] : []),
  ];

  return (
    <div className="space-y-4">
      {msgs.map((m) => (
        <SystemBanner key={m.id} message={m} onDismiss={onDismissMessage} />
      ))}
      <div className="flex space-x-1 bg-white rounded-lg shadow-sm border border-slate-200 p-1 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTgTab(t.id)}
            className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${tgTab === t.id ? "bg-cyan-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          >
            <t.icon className="w-4 h-4 mr-1.5" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Violations tab */}
      {tgTab === "violations" && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500">
              {violationsTotal} total violations
            </span>
          </div>
          {vLoading && (
            <div className="flex items-center gap-2 text-sm text-slate-400 py-6 justify-center">
              <RefreshCw className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {!vLoading && violations.length === 0 && (
            <div className="flex flex-col items-center py-16 text-slate-400">
              <Check className="w-10 h-10 mb-2" />
              <p className="text-sm">No violations to review</p>
            </div>
          )}
          {violations.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
              {violations.map((v) => {
                const reasons = Array.isArray(v.violation_reasons)
                  ? v.violation_reasons
                  : [];
                return (
                  <div key={v.id} className="px-4 py-3">
                    <div
                      className="flex items-start gap-3 cursor-pointer"
                      onClick={() =>
                        setExpandedV(expandedV === v.id ? null : v.id)
                      }
                    >
                      <span
                        className={`mt-0.5 px-2 py-0.5 rounded text-xs font-semibold shrink-0 ${ACTION_COLORS[v.escalation_action] || "bg-slate-100 text-slate-600"}`}
                      >
                        {v.escalation_action || "warn"}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-800">
                            {reasons.length > 0
                              ? reasons.join(", ")
                              : "Violation"}
                          </span>
                          {v.username && (
                            <span className="text-xs text-slate-500">
                              @{v.username}
                            </span>
                          )}
                          {v.warning_count > 1 && (
                            <span className="text-xs text-orange-500">
                              {v.warning_count} warnings
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">
                          {v.message_text || "—"}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {new Date(v.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {v.user_id && isAdmin && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnban(v);
                            }}
                            disabled={unbanningId === v.id}
                            className="px-2.5 py-1 text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200 rounded-lg hover:bg-orange-100 disabled:opacity-50 transition-colors"
                          >
                            {unbanningId === v.id ? "…" : "Unban"}
                          </button>
                        )}
                      </div>
                    </div>
                    {expandedV === v.id && v.message_text && (
                      <div className="mt-2 ml-16 p-3 bg-slate-50 rounded-lg text-sm text-slate-700 whitespace-pre-wrap">
                        {v.message_text}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Posts tab */}
      {tgTab === "posts" && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500">{postsTotal} posts</span>
            <div className="flex gap-1">
              {[
                ["", "All"],
                ["capture", "Captured"],
                ["log", "Logged"],
              ].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setPostsSource(val)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-lg border transition-colors ${
                    postsSource === val
                      ? "bg-cyan-600 border-cyan-600 text-white"
                      : "border-slate-300 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setPostsRefresh((n) => n + 1)}
              disabled={postsLoading}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              <RefreshCw
                className={`w-3.5 h-3.5 ${postsLoading ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
          </div>
          {postsLoading && (
            <div className="flex items-center gap-2 text-sm text-slate-400 py-6 justify-center">
              <RefreshCw className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {!postsLoading && posts.length === 0 && (
            <div className="flex flex-col items-center py-16 text-slate-400">
              <MessageSquare className="w-10 h-10 mb-2" />
              <p className="text-sm">No posts recorded</p>
            </div>
          )}
          {posts.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Sender
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Content
                    </th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                      Date
                    </th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {posts.map((p) => (
                    <tr
                      key={p.id}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 py-3 text-slate-700 font-medium whitespace-nowrap">
                        {p.sender_name || (
                          <span className="text-slate-400">—</span>
                        )}
                        {p.source === "log" && (
                          <span className="ml-2 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-500 rounded">
                            logged
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600 max-w-xs">
                        <span className="line-clamp-2">
                          {p.notes || <span className="text-slate-400">—</span>}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">
                        {p.created_at
                          ? new Date(p.created_at).toLocaleString()
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center gap-1.5 justify-end">
                          <button
                            onClick={() => handleOpenQa(p)}
                            className="px-2.5 py-1 text-xs font-medium bg-cyan-50 text-cyan-700 border border-cyan-200 rounded-lg hover:bg-cyan-100 transition-colors"
                          >
                            Add Q&amp;A
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => handleDeletePost(p.id)}
                              disabled={deletingPostId === p.id}
                              className="px-2.5 py-1 text-xs font-medium bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
                            >
                              {deletingPostId === p.id ? "…" : "Delete"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Q&A Modal */}
          {qaModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-800">
                    Add Q&amp;A Entry
                  </h3>
                  <button
                    onClick={() => setQaModal(null)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <form onSubmit={handleSaveQa} className="p-5 space-y-4">
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs text-slate-600 space-y-0.5">
                    <span className="font-medium text-slate-700">
                      Source post
                    </span>
                    <p className="mt-1 line-clamp-3">{qaModal.notes || "—"}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                      Question
                    </label>
                    <input
                      type="text"
                      required
                      value={qaForm.question}
                      onChange={(e) =>
                        setQaForm({ ...qaForm, question: e.target.value })
                      }
                      placeholder="e.g. What are your hours?"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                      Answer
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={qaForm.answer}
                      onChange={(e) =>
                        setQaForm({ ...qaForm, answer: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 outline-none resize-none"
                    />
                  </div>
                  {qaMsg && (
                    <p
                      className={`text-xs ${qaMsg.type === "ok" ? "text-green-600" : "text-red-600"}`}
                    >
                      {qaMsg.text}
                    </p>
                  )}
                  <div className="flex justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => setQaModal(null)}
                      className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={qaSaving}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white text-sm font-medium rounded-lg hover:bg-cyan-700 disabled:opacity-50 transition-colors"
                    >
                      {qaSaving ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      {qaSaving ? "Saving…" : "Save Q&A"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Config tab (admin only) */}
      {tgTab === "config" && isAdmin && (
        <>
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            {cfgLoading && (
              <div className="text-sm text-slate-400 flex items-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" /> Loading…
              </div>
            )}
            {cfgError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {cfgError}
              </div>
            )}
            <WizardShell
              accent="cyan"
              busy={cfgSaving}
              onFinish={handleSaveCfg}
              finishLabel={cfgSaved ? "Saved" : "Save Config"}
              steps={[
                {
                  key: "group",
                  title: "Group",
                  nextDisabled: !cfg.group_id?.trim(),
                  content: (
                    <div className="space-y-4">
                      <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600 space-y-1.5">
                        <p className="text-sm font-semibold text-slate-700">
                          Connect the bot to your group
                        </p>
                        <p>
                          1. Add the Hyve Telegram bot to your group as an admin
                          — your account manager can share the bot handle.
                        </p>
                        <p>
                          2. Find your group's ID: forward any group message to
                          @userinfobot, or copy the number from the group URL in
                          Telegram Web and prefix it with -100.
                        </p>
                        <p>3. Paste the group ID below.</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">
                          Telegram Group ID
                        </label>
                        <input
                          type="text"
                          value={cfg.group_id}
                          onChange={(e) =>
                            setCfg({ ...cfg, group_id: e.target.value })
                          }
                          placeholder="-1001234567890"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-cyan-500 outline-none"
                        />
                      </div>
                    </div>
                  ),
                },
                {
                  key: "modes",
                  title: "Modes",
                  content: (
                    <div>
                      <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                        Workflow modes
                      </p>
                      <p className="text-xs text-slate-400 mb-3">
                        Modes are combinable — the bot runs every enabled mode
                        on each group message.
                      </p>
                      <div className="space-y-2">
                        {[
                          {
                            key: "moderation",
                            label: "Moderation",
                            desc: "Watches for prohibited words and rate abuse; warns, mutes, then bans repeat offenders.",
                          },
                          {
                            key: "qa",
                            label: "Answer questions",
                            desc: "Replies when the bot is @-mentioned or someone replies to it, using the facility's Telegram agent.",
                          },
                          {
                            key: "logging",
                            label: "Log conversations",
                            desc: "Records every group message to the Posts tab (marked as logged).",
                          },
                        ].map((m) => (
                          <div
                            key={m.key}
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                          >
                            <div className="pr-4">
                              <p className="text-sm font-medium text-slate-700">
                                {m.label}
                              </p>
                              <p className="text-xs text-slate-500">{m.desc}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setMode(m.key, !cfg.modes?.[m.key])
                              }
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${cfg.modes?.[m.key] ? "bg-cyan-600" : "bg-slate-300"}`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${cfg.modes?.[m.key] ? "translate-x-6" : "translate-x-1"}`}
                              />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ),
                },
                ...(cfg.modes?.moderation
                  ? [
                      {
                        key: "moderation",
                        title: "Moderation",
                        content: (
                          <div className="space-y-4 border border-slate-200 rounded-lg p-4">
                            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                              Moderation settings
                            </p>
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">
                                Daily Message Limit
                              </label>
                              <input
                                type="number"
                                min={1}
                                max={10000}
                                value={cfg.daily_message_limit}
                                onChange={(e) =>
                                  setCfg({
                                    ...cfg,
                                    daily_message_limit:
                                      parseInt(e.target.value) || 1,
                                  })
                                }
                                className="w-40 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 outline-none"
                              />
                              <p className="text-xs text-slate-400 mt-1">
                                Messages per user per day before rate limiting
                                kicks in.
                              </p>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">
                                Prohibited Words
                              </label>
                              <textarea
                                rows={5}
                                value={(cfg.prohibited_words || []).join("\n")}
                                onChange={(e) =>
                                  setCfg({
                                    ...cfg,
                                    prohibited_words: e.target.value
                                      .split("\n")
                                      .map((w) => w.trim().toLowerCase())
                                      .filter(Boolean),
                                  })
                                }
                                placeholder={"spam\nscam\nnsfw"}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-cyan-500 outline-none resize-y"
                              />
                              <p className="text-xs text-slate-400 mt-1">
                                One word or phrase per line. Matched
                                case-insensitively.
                              </p>
                            </div>
                          </div>
                        ),
                      },
                    ]
                  : []),
                {
                  key: "review",
                  title: "Review & Save",
                  content: (
                    <div className="space-y-3">
                      <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 space-y-1">
                        <p>
                          <span className="text-slate-400">Group ID:</span>{" "}
                          <span className="font-mono">
                            {cfg.group_id || "—"}
                          </span>
                        </p>
                        <p>
                          <span className="text-slate-400">Enabled modes:</span>{" "}
                          {[
                            ["moderation", "Moderation"],
                            ["qa", "Answer questions"],
                            ["logging", "Log conversations"],
                          ]
                            .filter(([k]) => cfg.modes?.[k])
                            .map(([, label]) => label)
                            .join(", ") || "none"}
                        </p>
                        {cfg.modes?.moderation && (
                          <>
                            <p>
                              <span className="text-slate-400">
                                Daily message limit:
                              </span>{" "}
                              {cfg.daily_message_limit}
                            </p>
                            <p>
                              <span className="text-slate-400">
                                Prohibited words:
                              </span>{" "}
                              {(cfg.prohibited_words || []).length}
                            </p>
                          </>
                        )}
                      </div>
                      {cfgSaved && (
                        <p className="text-xs text-green-600">
                          Configuration saved.
                        </p>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </div>

          {/* Redis monitoring reset */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 mt-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-1">
              Clear Monitoring Data
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Resets daily message counters, rate limits, and other ephemeral
              Telegram monitoring state stored in Redis for this facility.
            </p>
            {clearRedisMsg && (
              <p className="text-xs text-slate-600 mb-2">{clearRedisMsg}</p>
            )}
            <button
              type="button"
              onClick={handleClearRedis}
              disabled={clearingRedis}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
            >
              {clearingRedis ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              {clearingRedis ? "Clearing…" : "Clear Redis Data"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
